import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DynamicSidebar from '../../../../components/ui/DynamicSidebar';
import DashboardHeader from '../../../../components/ui/DashboardHeader';
import Icon from '../../../../components/AppIcon';
import { format } from 'date-fns';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-warning/10 text-warning border-warning/20', icon: 'Clock' },
  confirmed: { label: 'Confirmed', color: 'bg-primary/10 text-primary border-primary/20', icon: 'CheckCircle' },
  paid: { label: 'Paid', color: 'bg-success/10 text-success border-success/20', icon: 'BadgeCheck' },
};

const InfoRow = ({ label, value, mono = false, highlight = false }) => (
  <div className="flex flex-col sm:flex-row sm:items-start py-3 border-b border-border last:border-0">
    <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide sm:w-52 shrink-0 mb-1 sm:mb-0">{label}</span>
    <span className={`text-sm break-all ${mono ? 'font-mono' : ''} ${highlight ? 'font-semibold text-text-primary' : 'text-text-primary'}`}>
      {value ?? '-'}
    </span>
  </div>
);

const SectionCard = ({ title, icon, children }) => (
  <div className="bg-surface border border-border rounded-xl overflow-hidden mb-5">
    <div className="flex items-center space-x-2 px-5 py-4 border-b border-border bg-muted/30">
      <Icon name={icon} size={18} className="text-primary" />
      <h2 className="text-sm font-semibold text-text-primary">{title}</h2>
    </div>
    <div className="px-5 py-2">{children}</div>
  </div>
);

const SettlementDetailPage = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const settlement = location?.state?.settlement || {};

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try { return format(new Date(dateStr), 'MMM dd, yyyy'); } catch { return dateStr; }
  };

  const formatCurrency = (amount) => {
    if (amount == null) return '-';
    return new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' })?.format(amount);
  };

  const statusInfo = STATUS_CONFIG?.[settlement?.status] || STATUS_CONFIG?.pending;

  const merchants = settlement?.merchants || [];
  const totalDeposits = merchants?.reduce((s, m) => s + (m?.deposits || 0), 0);
  const totalPayouts = merchants?.reduce((s, m) => s + (m?.payouts || 0), 0);
  const totalRefunds = merchants?.reduce((s, m) => s + (m?.refunds || 0), 0);
  const totalMdr = merchants?.reduce((s, m) => s + (m?.mdr || 0), 0);
  const netAmount = settlement?.net_amount ?? (totalDeposits - totalPayouts - totalRefunds - totalMdr);

  return (
    <div className="min-h-screen bg-background">
      <DashboardHeader onMenuToggle={() => setSidebarCollapsed(prev => !prev)} />
      <div className="flex">
        <DynamicSidebar
          isCollapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(prev => !prev)}
        />
        <main
          className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'} mt-16 p-6`}
        >
          {/* Breadcrumb & Back */}
          <div className="mb-6">
            <div className="flex items-center space-x-2 text-sm text-text-secondary mb-3">
              <span>Payment Operations</span>
              <Icon name="ChevronRight" size={14} />
              <button
                onClick={() => navigate('/payment-operations/settlements')}
                className="hover:text-primary transition-colors"
              >
                Settlements
              </button>
              <Icon name="ChevronRight" size={14} />
              <span className="text-text-primary font-medium">{settlement?.settlement_id || 'Detail'}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => navigate('/payment-operations/settlements')}
                  className="flex items-center space-x-1 text-sm text-text-secondary hover:text-primary transition-colors"
                >
                  <Icon name="ArrowLeft" size={16} />
                  <span>Back</span>
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-text-primary">Settlement Details</h1>
                  <p className="text-text-secondary text-sm mt-0.5">{settlement?.settlement_id || '-'}</p>
                </div>
              </div>
              <span className={`inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${statusInfo?.color}`}>
                <Icon name={statusInfo?.icon} size={13} />
                <span>{statusInfo?.label}</span>
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
            {/* Net Amount Card */}
            <div className="bg-surface border border-border rounded-xl p-5 flex flex-col justify-between">
              <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">Net Settlement Amount</p>
              <p className="text-3xl font-bold text-text-primary">{formatCurrency(netAmount)}</p>
              <p className="text-xs text-text-secondary mt-2">Deposits − Payouts − Refunds − MDR</p>
            </div>
            <div className="bg-surface border border-border rounded-xl p-5">
              <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-3">Date Range</p>
              <div className="flex items-center space-x-2">
                <Icon name="Calendar" size={16} className="text-primary" />
                <span className="text-sm text-text-primary font-medium">
                  {formatDate(settlement?.date_from)} – {formatDate(settlement?.date_to)}
                </span>
              </div>
            </div>
            <div className="bg-surface border border-border rounded-xl p-5">
              <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-3">Business</p>
              <div className="flex items-center space-x-2">
                <Icon name="Building2" size={16} className="text-primary" />
                <span className="text-sm text-text-primary font-medium">{settlement?.business_name || '-'}</span>
              </div>
              <p className="text-xs text-text-secondary mt-1 ml-6">{settlement?.business_id || ''}</p>
            </div>
          </div>

          {/* Summary Breakdown */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
            {[
              { label: 'Total Deposits', value: totalDeposits, icon: 'TrendingUp', color: 'text-success' },
              { label: 'Total Payouts', value: totalPayouts, icon: 'TrendingDown', color: 'text-error' },
              { label: 'Total Refunds', value: totalRefunds, icon: 'RotateCcw', color: 'text-warning' },
              { label: 'Total MDR', value: totalMdr, icon: 'Percent', color: 'text-text-secondary' },
            ]?.map(card => (
              <div key={card?.label} className="bg-surface border border-border rounded-xl p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name={card?.icon} size={15} className={card?.color} />
                  <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide">{card?.label}</p>
                </div>
                <p className="text-lg font-bold text-text-primary">{formatCurrency(card?.value)}</p>
              </div>
            ))}
          </div>

          {/* Settlement Info */}
          <SectionCard title="Settlement Information" icon="FileText">
            <InfoRow label="Settlement ID" value={settlement?.settlement_id} mono />
            <InfoRow label="Status" value={statusInfo?.label} />
            <InfoRow label="Business" value={settlement?.business_name} />
            <InfoRow label="Business ID" value={settlement?.business_id} mono />
            <InfoRow label="Date Range" value={`${formatDate(settlement?.date_from)} – ${formatDate(settlement?.date_to)}`} />
            <InfoRow label="Created At" value={formatDate(settlement?.created_at)} />
            <InfoRow label="Net Amount" value={formatCurrency(netAmount)} highlight />
          </SectionCard>

          {/* Merchant Breakdown */}
          <SectionCard title="Merchant Breakdown" icon="Users">
            {merchants?.length === 0 ? (
              <div className="py-8 text-center text-text-secondary text-sm">No merchant data available</div>
            ) : (
              <div className="overflow-x-auto -mx-5">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      {['Merchant', 'Merchant ID', 'Deposits', 'Payouts', 'Refunds', 'MDR', 'Net']?.map(col => (
                        <th key={col} className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider whitespace-nowrap">
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {merchants?.map((m, idx) => {
                      const net = (m?.deposits || 0) - (m?.payouts || 0) - (m?.refunds || 0) - (m?.mdr || 0);
                      return (
                        <tr key={idx} className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors">
                          <td className="px-4 py-3 text-sm font-medium text-text-primary whitespace-nowrap">{m?.merchant_name}</td>
                          <td className="px-4 py-3 text-xs font-mono text-text-secondary">{m?.merchant_id}</td>
                          <td className="px-4 py-3 text-sm text-success font-medium">{formatCurrency(m?.deposits)}</td>
                          <td className="px-4 py-3 text-sm text-error">{formatCurrency(m?.payouts)}</td>
                          <td className="px-4 py-3 text-sm text-warning">{formatCurrency(m?.refunds)}</td>
                          <td className="px-4 py-3 text-sm text-text-secondary">{formatCurrency(m?.mdr)}</td>
                          <td className="px-4 py-3 text-sm font-bold text-text-primary">{formatCurrency(net)}</td>
                        </tr>
                      );
                    })}
                    {/* Totals row */}
                    <tr className="bg-muted/30 border-t-2 border-border">
                      <td className="px-4 py-3 text-xs font-bold text-text-primary uppercase tracking-wide" colSpan={2}>Totals</td>
                      <td className="px-4 py-3 text-sm font-bold text-success">{formatCurrency(totalDeposits)}</td>
                      <td className="px-4 py-3 text-sm font-bold text-error">{formatCurrency(totalPayouts)}</td>
                      <td className="px-4 py-3 text-sm font-bold text-warning">{formatCurrency(totalRefunds)}</td>
                      <td className="px-4 py-3 text-sm font-bold text-text-secondary">{formatCurrency(totalMdr)}</td>
                      <td className="px-4 py-3 text-sm font-bold text-text-primary">{formatCurrency(netAmount)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </main>
      </div>
    </div>
  );
};

export default SettlementDetailPage;
